import { z } from "zod";
import { emailSchema } from "../usuarios/usuarios";

/**
 * Contratos de clientes (as clinicas atendidas pela Monte) e dos vinculos entre
 * pessoas e clientes. Cada cliente e um tenant: seus dados so aparecem para
 * quem tem vinculo com ele ou para a equipe Monte.
 */

export const PAPEIS_NO_CLIENTE = ["gestor", "membro"] as const;
export const papelNoClienteSchema = z.enum(PAPEIS_NO_CLIENTE);
export type PapelNoCliente = z.infer<typeof papelNoClienteSchema>;

/** Escopos da equipe Monte: enxergar e gerenciar todos os clientes. */
export const ESCOPOS_DE_CLIENTES = {
  ler: "clientes:ler",
  escrever: "clientes:escrever",
} as const;

const nomeDoClienteSchema = z
  .string()
  .trim()
  .min(2, "minimo de 2 caracteres")
  .max(120, "maximo de 120 caracteres");

const textoCurto = (maximo: number) =>
  z
    .string()
    .trim()
    .max(maximo, `maximo de ${maximo} caracteres`)
    .transform((valor) => (valor.length === 0 ? null : valor))
    .nullable();

export const clienteSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  ativo: z.boolean(),
  especialidade: z.string().nullable(),
  cidade: z.string().nullable(),
  responsavelNaMonte: z.string().nullable(),
  /** Papel de quem pediu neste cliente. `null` para a equipe Monte, que ve todos. */
  meuPapel: papelNoClienteSchema.nullable(),
  criadoEm: z.iso.datetime(),
  atualizadoEm: z.iso.datetime(),
});
export type Cliente = z.infer<typeof clienteSchema>;

/**
 * Lista sem paginacao, de proposito: o seletor da barra lateral precisa de
 * todos os clientes de uma vez, e a Monte trabalha com poucos projetos.
 */
export const listaDeClientesSchema = z.object({ dados: z.array(clienteSchema) });
export type ListaDeClientes = z.infer<typeof listaDeClientesSchema>;

export const criarClienteSchema = z.object({ nome: nomeDoClienteSchema }).strict();
export type CriarCliente = z.infer<typeof criarClienteSchema>;

export const atualizarClienteSchema = z
  .object({
    nome: nomeDoClienteSchema.optional(),
    ativo: z.boolean().optional(),
    especialidade: textoCurto(80).optional(),
    cidade: textoCurto(80).optional(),
    responsavelNaMonte: textoCurto(120).optional(),
  })
  .strict()
  .refine((dados) => Object.values(dados).some((valor) => valor !== undefined), {
    message: "informe ao menos um campo para alterar",
  });
export type AtualizarCliente = z.infer<typeof atualizarClienteSchema>;

export const membroSchema = z.object({
  usuarioId: z.uuid(),
  nome: z.string(),
  email: z.string(),
  papel: papelNoClienteSchema,
  /** Conta ativa no sistema (desativar a conta tira o acesso a todos os clientes). */
  ativo: z.boolean(),
  senhaDefinida: z.boolean(),
  adicionadoEm: z.iso.datetime(),
});
export type Membro = z.infer<typeof membroSchema>;

export const listaDeMembrosSchema = z.object({ dados: z.array(membroSchema) });
export type ListaDeMembros = z.infer<typeof listaDeMembrosSchema>;

/**
 * POST de acesso: `linkDeSenha` vem quando a conta acabou de ser criada.
 * Pessoa que ja tinha conta entra sem novo convite (`null`).
 */
export const membroComConviteSchema = membroSchema.extend({
  linkDeSenha: z.url().nullable(),
});
export type MembroComConvite = z.infer<typeof membroComConviteSchema>;

/**
 * Adiciona uma pessoa ao cliente. Se o e-mail ainda nao tem conta, ela e criada
 * e recebe o convite; se ja tem, so ganha o vinculo.
 */
export const adicionarMembroSchema = z
  .object({
    nome: z.string().trim().min(3, "minimo de 3 caracteres").max(120, "maximo de 120 caracteres"),
    email: emailSchema,
    papel: papelNoClienteSchema.default("membro"),
  })
  .strict();
export type AdicionarMembro = z.infer<typeof adicionarMembroSchema>;

export const alterarMembroSchema = z.object({ papel: papelNoClienteSchema }).strict();
export type AlterarMembro = z.infer<typeof alterarMembroSchema>;
